import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn.js';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  /// Alerta que não impede salvar: o valor provavelmente está errado, mas pode ser intencional.
  /// Cede lugar ao erro e substitui a dica enquanto existir.
  warning?: string | undefined;
  leftIcon?: ReactNode | undefined;
  rightIcon?: ReactNode | undefined;
  /// Botão que age sobre o que foi digitado. Fica ao lado do campo, e não abaixo do rótulo, para
  /// continuar alinhado quando a dica ou o erro mudam a altura do bloco.
  action?: ReactNode | undefined;
  /// Rótulo só para leitor de tela, quando a linha ao lado já diz o que é o campo.
  hideLabel?: boolean | undefined;
  /// Ao lado do rótulo, como a ajuda contextual do campo.
  labelAddon?: ReactNode | undefined;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      action,
      className,
      error,
      hideLabel = false,
      hint,
      id: providedId,
      label,
      labelAddon,
      leftIcon,
      rightIcon,
      warning,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const descriptionId = error
      ? `${id}-error`
      : warning
        ? `${id}-warning`
        : hint
          ? `${id}-hint`
          : undefined;
    return (
      <div className={hideLabel ? undefined : 'space-y-2'}>
        {labelAddon && !hideLabel ? (
          <span className="flex items-center gap-1">
            <label htmlFor={id} className="block text-xs font-semibold text-content/70">
              {label}
            </label>
            {labelAddon}
          </span>
        ) : (
          <label
            htmlFor={id}
            className={cn('block text-xs font-semibold text-content/70', hideLabel && 'sr-only')}
          >
            {label}
          </label>
        )}
        <div className={action ? 'flex min-w-0 items-center gap-2' : undefined}>
          <div className="relative min-w-0 flex-1">
            {leftIcon ? (
              <span className="pointer-events-none absolute inset-y-0 left-0 grid w-11 place-items-center text-content-faint">
                {leftIcon}
              </span>
            ) : null}
            <input
              ref={ref}
              id={id}
              aria-invalid={Boolean(error)}
              aria-describedby={descriptionId}
              className={cn(
                'hue-input h-12 w-full rounded-xl border bg-surface-elevated px-4 text-sm text-content outline-none transition placeholder:text-content-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20',
                leftIcon && 'pl-11',
                rightIcon && 'pr-11',
                error
                  ? 'border-danger/70'
                  : warning
                    ? 'border-warning/70'
                    : 'border-surface-border',
                className,
              )}
              {...props}
            />
            {rightIcon ? (
              <span className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-content-muted">
                {rightIcon}
              </span>
            ) : null}
          </div>
          {action ? <span className="shrink-0">{action}</span> : null}
        </div>
        {error ? (
          <p id={descriptionId} role="alert" className="text-xs text-danger">
            {error}
          </p>
        ) : warning ? (
          <p id={descriptionId} className="text-xs leading-5 text-warning">
            {warning}
          </p>
        ) : hint ? (
          <p id={descriptionId} className="text-xs leading-5 text-content-muted">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
